import { useAuthLogout } from "@api/generated/auth/auth";
import type { Me } from "@api/generated/model";
import { useUserVersion } from "@api/generated/user/user";
import { Icon } from "@app/icons";
import { HrefLink } from "@app/links";
import { type NavSection, SECTIONS, sectionOf } from "@app/nav";
import { hasRole, usePins, useSaveSetting, useSeason, useSettings } from "@app/workspace";
import { isActiveJob, useMyJobs } from "@features/system/jobs";
import { currentLanguage, LANGUAGES } from "@i18n";
import { Avatar, Kbd, Menu, Tooltip, useMantineColorScheme } from "@mantine/core";
import { useDisclosure, useHotkeys } from "@mantine/hooks";
import { Link, useRouterState } from "@tanstack/react-router";
import { LoadingBar } from "@ui/LoadingBar";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommandPalette } from "./CommandPalette";
import { ShellContext, useShell } from "./context";
import { DeleteSeasonModal, NewSeasonModal, ShortcutsModal } from "./Dialogs";
import { SeasonMenuDropdown } from "./SeasonChip";
import css from "./shell.module.css";

/** The frame of every screen: icon rail, section panel, top bar; plus the global dialogs and shortcuts. */
export function AppShell({ me, children }: { me: Me; children: ReactNode }) {
  const [palette, paletteCtl] = useDisclosure(false);
  const [shortcuts, shortcutsCtl] = useDisclosure(false);
  const [newSeason, newSeasonCtl] = useDisclosure(false);
  const [deleteSeason, deleteSeasonCtl] = useDisclosure(false);
  const [seasonOpen, setSeasonOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const navigating = useRouterState({ select: (s) => s.isLoading });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const settings = useSettings();
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  // appearance preferences follow the account (Beállítások → Megjelenés)
  useEffect(() => {
    if (!settings) return;
    // Reapplying the same scheme disables all CSS transitions during every route change.
    if (colorScheme !== settings.theme) setColorScheme(settings.theme);
    document.documentElement.dataset.fontSize = settings.font_size;
    document.documentElement.dataset.density = settings.density;
  }, [settings, colorScheme, setColorScheme]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the narrow-screen panel closes after every navigation
  useEffect(() => {
    setPanelOpen(false);
  }, [pathname]);

  const api = useMemo(
    () => ({
      openPalette: paletteCtl.open,
      openShortcuts: shortcutsCtl.open,
      openNewSeason: newSeasonCtl.open,
      openDeleteSeason: deleteSeasonCtl.open,
      openSeason: () => setSeasonOpen(true),
      seasonOpen,
      setSeasonOpen,
    }),
    [paletteCtl.open, shortcutsCtl.open, newSeasonCtl.open, deleteSeasonCtl.open, seasonOpen],
  );
  // "#" (Parcellakód beolvasása): the quick search also opens a scanned plot code
  useHotkeys([
    ["mod+K", paletteCtl.open],
    ["shift+?", shortcutsCtl.open],
    ["shift+3", paletteCtl.open],
  ]);

  return (
    <ShellContext.Provider value={api}>
      <div className={css.layout}>
        <Rail pathname={pathname} onToggle={() => setPanelOpen((o) => !o)} />
        <SidePanel pathname={pathname} open={panelOpen} />
        <div className={css.main}>
          <Topbar me={me} pathname={pathname} />
          <LoadingBar active={navigating} />
          <main className={css.content} id="main">
            {children}
          </main>
        </div>
      </div>
      <CommandPalette opened={palette} onClose={paletteCtl.close} />
      <ShortcutsModal opened={shortcuts} onClose={shortcutsCtl.close} />
      {newSeason && <NewSeasonModal onClose={newSeasonCtl.close} />}
      {deleteSeason && <DeleteSeasonModal onClose={deleteSeasonCtl.close} />}
    </ShellContext.Provider>
  );
}

function Rail({ pathname, onToggle }: { pathname: string; onToggle: () => void }) {
  const { t } = useTranslation();
  const current = sectionOf(pathname).section.key;
  const button = (s: NavSection) => (
    <Tooltip key={s.key} label={t(`nav.${s.key}.title`)} position="right">
      <Link
        to={s.items[0]?.to ?? "/"}
        aria-label={t(`nav.${s.key}.title`)}
        className={`${css.railBtn} ${current === s.key ? css.on : ""}`}
        onClick={(e) => {
          if (current === s.key && window.matchMedia("(max-width: 900px)").matches) {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <Icon name={s.icon} size={22} />
      </Link>
    </Tooltip>
  );
  return (
    <nav className={css.rail} aria-label={t("nav.mainAreas")}>
      <Link to="/" className={css.logo} aria-label={t("nav.homeLink")}>
        <Icon name="wheat" size={24} />
      </Link>
      {SECTIONS.filter((s) => !s.bottom).map(button)}
      <div style={{ flexGrow: 1 }} />
      {SECTIONS.filter((s) => s.bottom).map(button)}
    </nav>
  );
}

function SidePanel({ pathname, open }: { pathname: string; open: boolean }) {
  const { t } = useTranslation();
  const { section, item: active } = sectionOf(pathname);
  const { pins } = usePins();
  const version = useUserVersion();
  return (
    <div className={`${css.panel} ${open ? css.open : ""}`}>
      <div className={`sec ${css.panelTitle}`}>{t(`nav.${section.key}.title`)}</div>
      {section.items.map((it) => (
        <Link key={it.id} to={it.to} className={`${css.navItem} ${active?.to === it.to ? css.on : ""}`}>
          {t(`nav.${section.key}.${it.id}`)}
        </Link>
      ))}
      <div className={css.divider} />
      <div className={`sec ${css.panelTitle}`} style={{ paddingBottom: 8 }}>
        {t("nav.pinned")}
      </div>
      {pins.length === 0 && (
        <span className="sm mu" style={{ padding: "0 10px" }}>
          {t("nav.noPins")}
        </span>
      )}
      <nav aria-label={t("nav.pinned")} style={{ display: "contents" }}>
        {pins.map((p) => (
          <HrefLink key={p.href} href={p.href} className={css.pin}>
            <span className={css.pinTitle}>{p.title}</span>
            {p.subtitle && <span className={css.pinSub}>{p.subtitle}</span>}
          </HrefLink>
        ))}
      </nav>
      <div style={{ flexGrow: 1 }} />
      <div className={css.footer}>
        <div>{t("shell.versionLabel", { v: __WEB_VERSION__ })}</div>
        {version.data && <div>{t("shell.apiVersion", { v: version.data.version })}</div>}
        {version.data?.commit && (
          <div style={{ fontFamily: "var(--b-mono)" }} title={t("shell.commitHelp")}>
            {version.data.commit}
          </div>
        )}
      </div>
    </div>
  );
}

function Topbar({ me, pathname }: { me: Me; pathname: string }) {
  const { t } = useTranslation();
  const { section, item } = sectionOf(pathname);
  const version = useUserVersion();
  const env = version.data?.environment ?? "dev";
  const logout = useAuthLogout({ mutation: { onSuccess: (r) => window.location.assign(r.logout_url) } });
  const saveSetting = useSaveSetting();
  const running = (useMyJobs().data ?? []).filter(isActiveJob).length;
  const jobsLabel = running ? t("shell.jobsRunning", { count: running }) : t("shell.jobs");
  const name = me.full_name ?? me.username;
  const crumb = [t(`nav.${section.key}.title`), item && item.to !== "/" ? t(`nav.${section.key}.${item.id}`) : null]
    .filter(Boolean)
    .join(" / ");
  return (
    <header className={css.topbar}>
      <div className={css.crumb}>{crumb}</div>
      <SearchButton />
      <div style={{ flexGrow: 1 }} />
      <SeasonMenu roles={me.roles} />
      {env !== "prod" && (
        <span className={`${css.env} ${env === "test" ? css.test : ""}`}>{t(`app.environment.${env}`, { defaultValue: env })}</span>
      )}
      <Tooltip label={jobsLabel}>
        <Link to="/system/jobs" className={css.iconBtn} aria-label={jobsLabel}>
          <Icon name="jobs" />
          {running > 0 && <span className={css.badge}>{running}</span>}
        </Link>
      </Tooltip>
      <ShortcutsButton />
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <button type="button" className={css.userBtn} aria-label={`${name} · ${t("shell.userMenu")}`}>
            <Avatar aria-hidden="true" name={name} color="breeder" variant="filled" size={30} radius="xl" />
            <span style={{ fontSize: "var(--b-text-body)", fontWeight: 500 }}>{name}</span>
          </button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>{t("user.roles")}</Menu.Label>
          {me.roles.map((r) => (
            <Menu.Item key={r} disabled>
              {t(`roles.${r}`, { defaultValue: r })}
            </Menu.Item>
          ))}
          <Menu.Divider />
          <Menu.Label>{t("settings.language")}</Menu.Label>
          {LANGUAGES.map((l) => (
            <Menu.Item key={l} lang={l} onClick={() => saveSetting("language", l)} fw={l === currentLanguage() ? 700 : 400}>
              {t(`settings.languages.${l}`)}
            </Menu.Item>
          ))}
          <Menu.Divider />
          <Menu.Item component={Link} to="/system/settings">
            {t("user.settings")}
          </Menu.Item>
          <Menu.Item color="red" leftSection={<Icon name="logout" size={16} />} onClick={() => logout.mutate()}>
            {t("user.signOut")}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </header>
  );
}

function SearchButton() {
  const { t } = useTranslation();
  const api = useShell();
  return (
    <button type="button" className={css.searchBtn} onClick={api.openPalette}>
      <Icon name="search" size={16} />
      <span style={{ flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{t("shell.searchPlaceholder")}</span>
      <Kbd>Ctrl K</Kbd>
    </button>
  );
}

function ShortcutsButton() {
  const { t } = useTranslation();
  const api = useShell();
  return (
    <Tooltip label={t("shell.help")}>
      <button type="button" className={css.iconBtn} aria-label={t("shell.help")} onClick={api.openShortcuts}>
        <Icon name="help" />
      </button>
    </Tooltip>
  );
}

function SeasonMenu({ roles }: { roles: string[] }) {
  const { t } = useTranslation();
  const api = useShell();
  const { ev } = useSeason();
  useHotkeys([["alt+Y", api.openSeason]]);
  return (
    <Menu opened={api.seasonOpen} onChange={api.setSeasonOpen} position="bottom-end" withinPortal>
      <Menu.Target>
        <button
          type="button"
          className={css.yearBtn}
          aria-label={`${t("shell.year")} ${ev ?? "–"} · ${t("shell.switchYear")}`}
          title={t("shell.yearHelp")}
        >
          <span className="sm mu">{t("shell.year")}</span>
          <span style={{ fontSize: "var(--b-text-heading)", fontWeight: 700, fontFamily: "var(--b-mono)" }}>{ev ?? "–"}</span>
          <Icon name="chevronDown" size={14} />
        </button>
      </Menu.Target>
      <SeasonMenuDropdown>
        {hasRole(roles, "nemesito") && (
          <>
            <Menu.Divider />
            <Menu.Item leftSection={<Icon name="plus" size={16} />} onClick={api.openNewSeason}>
              {t("shell.openYear")}
            </Menu.Item>
          </>
        )}
        {hasRole(roles, "admin") && (
          <Menu.Item leftSection={<Icon name="trash" size={16} />} c="var(--b-red)" onClick={api.openDeleteSeason}>
            {t("shell.deleteYear")}
          </Menu.Item>
        )}
      </SeasonMenuDropdown>
    </Menu>
  );
}
